import { useMemo, useState, useEffect } from 'react';
import { Box, Text } from '@radix-ui/themes';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import type { PriceEntry } from '@/types';
import { formatPrice, getChartTime, isCurrentPeriod, formatTimeRange } from '@/lib/utils';

interface PriceChartProps {
  prices: PriceEntry[];
}

interface ChartDataPoint {
  time: string;
  price: number;
  deliveryStart: string;
  deliveryEnd: string;
  isCurrent: boolean;
}

function useXAxisAngle(): number {
  const [angle, setAngle] = useState(-45);

  useEffect(() => {
    const updateAngle = () => {
      const width = window.innerWidth;
      const isLandscape = window.innerWidth > window.innerHeight;

      if (width >= 768) {
        // Desktop and iPad
        setAngle(-45);
      } else if (isLandscape) {
        // Mobile landscape
        setAngle(-68);
      } else {
        // Mobile portrait
        setAngle(-90);
      }
    };

    updateAngle();
    window.addEventListener('resize', updateAngle);
    window.addEventListener('orientationchange', updateAngle);

    return () => {
      window.removeEventListener('resize', updateAngle);
      window.removeEventListener('orientationchange', updateAngle);
    };
  }, []);

  return angle;
}

export function PriceChart({ prices }: PriceChartProps) {
  const xAxisAngle = useXAxisAngle();
  const data = useMemo(
    (): ChartDataPoint[] =>
      prices.map((p) => ({
        time: getChartTime(p.deliveryStart),
        price: p.priceOreKwh,
        deliveryStart: p.deliveryStart,
        deliveryEnd: p.deliveryEnd,
        isCurrent: isCurrentPeriod(p.deliveryStart, p.deliveryEnd),
      })),
    [prices]
  );

  const currentPoint = useMemo(() => data.find((d) => d.isCurrent), [data]);

  const minPrice = useMemo(() => Math.min(...data.map((d) => d.price)), [data]);

  const maxPrice = useMemo(() => Math.max(...data.map((d) => d.price)), [data]);

  const padding = useMemo(() => (maxPrice - minPrice) * 0.1, [maxPrice, minPrice]);

  return (
    <Box ml="1" style={{ width: '100%', height: 380, userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'pan-y pinch-zoom' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-a5)" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11, fill: 'var(--gray-11)' }}
            tickLine={{ stroke: 'var(--gray-a5)' }}
            axisLine={{ stroke: 'var(--gray-a5)' }}
            interval={3} // Show every 4th tick (hourly)
            angle={xAxisAngle}
            textAnchor="end"
            height={60}
          />
          <YAxis
            domain={[Math.max(0, minPrice - padding), maxPrice + padding]}
            tick={{ fontSize: 11, fill: 'var(--gray-11)' }}
            tickLine={{ stroke: 'var(--gray-a5)' }}
            axisLine={{ stroke: 'var(--gray-a5)' }}
            tickFormatter={(value) => formatPrice(value)}
            width={50}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || payload.length === 0) return null;
              const point = payload[0].payload as ChartDataPoint;
              return (
                <Box
                  style={{
                    backgroundColor: 'var(--color-panel-solid)',
                    border: '1px solid var(--gray-a5)',
                    borderRadius: 'var(--radius-2)',
                    padding: 'var(--space-2)',
                    boxShadow: 'var(--shadow-3)',
                  }}
                >
                  <Text size="2" weight="bold" as="div">
                    {formatPrice(point.price)} öre/kWh
                  </Text>
                  <Text size="1" color="gray" as="div">
                    {formatTimeRange(point.deliveryStart, point.deliveryEnd)}
                  </Text>
                  {point.isCurrent && (
                    <Text size="1" color="green" as="div">
                      Nu
                    </Text>
                  )}
                </Box>
              );
            }}
          />
          <Line
            type="stepAfter"
            dataKey="price"
            stroke="var(--accent-9)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: 'var(--accent-9)' }}
          />
          {currentPoint && (
            <ReferenceDot
              x={currentPoint.time}
              y={currentPoint.price}
              r={8}
              fill="var(--green-9)"
              stroke="var(--green-11)"
              strokeWidth={2}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
